import { apiFetch } from '../client';

/** Importaciones (una por contenedor). Server: server/src/routes/importaciones.ts. */
export type ImportacionStatus = 'Abierta' | 'Cerrada' | 'Anulada';
export type MonedaImportacion = 'USD' | 'ARS';

export type Importacion = {
  id: number;
  numero: number;
  descripcion: string;
  /** yyyy-mm-dd */
  fecha: string;
  /** yyyy-mm-dd */
  fechaProbable: string;
  /** La fecha probable al crearla (yyyy-mm-dd); no cambia. null = anterior a esta anotación. */
  fechaProbableOriginal: string | null;
  /** Días de `fecha` a la fecha probable al crearla; no cambia. */
  diasEstimadosOriginal: number | null;
  /** Id del proveedor en ABM_Proveedores. */
  fabricanteProveedorId: string | null;
  fabricante: string | null;
  moneda: MonedaImportacion;
  /** null = sin definir. */
  totalFob: number | null;
  status: ImportacionStatus;
  cantidadFacturas: number;
  /** Por moneda: una compra en dólares y un flete en pesos no se suman. */
  total: Record<string, number>;
  pendiente: Record<string, number>;
  /** Sólo compras del fabricante, en la moneda de la importación. */
  fobInstrumentado: number;
  /** Negativo = se instrumentó de más; null = FOB sin definir. */
  fobPorInstrumentar: number | null;
  /** Compras del fabricante en otra moneda que la importación: no suman al FOB (no se convierten). */
  fobOtraMoneda: { compras: number; montos: Record<string, number> };
  /** Costo del contenedor separado: mercadería y gastos de importación (netos sin IVA, por moneda). */
  desglose: DesgloseImportacion;
  /** Nombre de la etapa arribo: si hay, la fecha probable se calcula desde las etapas. */
  etapaArribo: string | null;
  /** La primera etapa sin completar; null = sin etapas o todas completadas. */
  etapaActual: { nombre: string; estado: EstadoEtapa; fin: string } | null;
  etapasTotal: number;
  etapasCompletas: number;
};

export type DesgloseImportacion = {
  mercaderia: Record<string, number>;
  gastos: Array<{ cuenta: string; montos: Record<string, number> }>;
  /** Compras sin líneas: no se pueden separar. */
  sinDetalle: Record<string, number>;
};

export type CompraImportacion = {
  id: string;
  proveedor: string;
  comprobante: string;
  numero: string;
  fecha: string | null;
  moneda: string;
  total: number;
  pagado: number;
  restante: number;
  status: string;
  facturaDriveItemId: string | null;
  facturaFileName: string | null;
  /** Neto sin IVA de las líneas de producto; null = la compra no tiene líneas. */
  productos: number | null;
  /** Neto sin IVA de las líneas de servicio, por cuenta contable. */
  gastos: Array<{ cuenta: string; importe: number }>;
};

export type PagoImportacion = {
  id: string;
  compraId: string;
  proveedor: string;
  fecha: string | null;
  moneda: string;
  importe: number;
};

export type ImportacionDetalle = Importacion & {
  etapas: EtapaImportacion[];
  compras: CompraImportacion[];
  pagos: PagoImportacion[];
};

export type ImportacionPayload = {
  /** Vacío = el siguiente del país (alta) o el que ya tiene (edición). */
  numero?: number;
  descripcion: string;
  fecha: string;
  fechaProbable: string;
  fabricanteProveedorId: string | null;
  moneda: MonedaImportacion;
  totalFob: number | null;
};

export async function listImportaciones(): Promise<Importacion[]> {
  const r = await apiFetch<{ items: Importacion[] }>('/api/importaciones');
  return r.items;
}

export function getImportacion(id: number): Promise<ImportacionDetalle> {
  return apiFetch<ImportacionDetalle>(`/api/importaciones/${id}`);
}

/** Número sugerido para el alta: el último del país + 1. */
export async function getSiguienteNumeroImportacion(): Promise<number> {
  const r = await apiFetch<{ numero: number }>('/api/importaciones/siguiente-numero');
  return r.numero;
}

export function crearImportacion(body: ImportacionPayload): Promise<{ id: number }> {
  return apiFetch<{ id: number }>('/api/importaciones', { method: 'POST', body });
}

export async function editarImportacion(id: number, body: ImportacionPayload): Promise<void> {
  await apiFetch(`/api/importaciones/${id}`, { method: 'PUT', body });
}

export async function cambiarEstadoImportacion(id: number, accion: 'cerrar' | 'reabrir' | 'anular'): Promise<void> {
  await apiFetch(`/api/importaciones/${id}/${accion}`, { method: 'POST' });
}

// ── Etapas (ciclo de vida) ───────────────────────────────────────────────

/** completada / vencida (sin completar y pasado su fin planificado) / en curso (la primera sin completar) / pendiente. */
export type EstadoEtapa = 'completada' | 'vencida' | 'en_curso' | 'pendiente';

export type EtapaImportacion = {
  id: number;
  orden: number;
  nombre: string;
  dias: number;
  avisarDiasAntes: number;
  esArribo: boolean;
  /** yyyy-mm-dd; con fecha real la etapa está completada. */
  fechaReal: string | null;
  /** Planificada: encadenada desde la fecha de la importación y las fechas reales. */
  inicio: string;
  fin: string;
  estado: EstadoEtapa;
  /** La primera sin completar. */
  actual: boolean;
};

export type EtapaPayload = {
  nombre: string;
  dias: number;
  avisarDiasAntes: number;
  esArribo: boolean;
};

const rutaEtapas = (id: number) => `/api/importaciones/${id}/etapas`;

export async function crearEtapa(id: number, body: EtapaPayload): Promise<void> {
  await apiFetch(rutaEtapas(id), { method: 'POST', body });
}

export async function editarEtapa(id: number, etapaId: number, body: EtapaPayload): Promise<void> {
  await apiFetch(`${rutaEtapas(id)}/${etapaId}`, { method: 'PUT', body });
}

/** Sin fecha, hoy. */
export async function completarEtapa(id: number, etapaId: number, fechaReal?: string): Promise<void> {
  await apiFetch(`${rutaEtapas(id)}/${etapaId}/completar`, { method: 'POST', body: fechaReal ? { fechaReal } : {} });
}

export async function deshacerEtapa(id: number, etapaId: number): Promise<void> {
  await apiFetch(`${rutaEtapas(id)}/${etapaId}/deshacer`, { method: 'POST' });
}

export async function moverEtapa(id: number, etapaId: number, direccion: 'subir' | 'bajar'): Promise<void> {
  await apiFetch(`${rutaEtapas(id)}/${etapaId}/mover`, { method: 'POST', body: { direccion } });
}

export async function eliminarEtapa(id: number, etapaId: number): Promise<void> {
  await apiFetch(`${rutaEtapas(id)}/${etapaId}`, { method: 'DELETE' });
}

export async function cargarEtapasDePlantilla(id: number): Promise<void> {
  await apiFetch(`${rutaEtapas(id)}/cargar-plantilla`, { method: 'POST' });
}

// ── Compras existentes ───────────────────────────────────────────────────

export type CompraDisponible = {
  id: string;
  proveedor: string;
  comprobante: string;
  numero: string;
  fecha: string | null;
  moneda: string;
  total: number;
  status: string;
  /** Es del fabricante de la importación (van primero). */
  delFabricante: boolean;
};

export async function listComprasDisponibles(id: number): Promise<CompraDisponible[]> {
  const r = await apiFetch<{ items: CompraDisponible[] }>(`/api/importaciones/${id}/compras-disponibles`);
  return r.items;
}

export function vincularCompras(
  id: number,
  compraIds: string[],
): Promise<{ ok: boolean; vinculadas: number; errores: Array<{ compraId: string; message: string }> }> {
  return apiFetch(`/api/importaciones/${id}/compras`, { method: 'POST', body: { compraIds } });
}

export async function desvincularCompra(id: number, compraId: string): Promise<void> {
  await apiFetch(`/api/importaciones/${id}/compras/${compraId}`, { method: 'DELETE' });
}

// ── Artículos (qué trae el contenedor) ──────────────────────────────────

export type ArticuloImportacion = {
  articleId: number | null;
  articulo: string;
  codigo: string | null;
  comprado: number;
  recibido: number;
  pendiente: number;
  /** En cuántas compras viene. */
  compras: number;
  /** Lo mismo abierto por compra. */
  detalle: Array<{
    compraId: string;
    proveedor: string;
    comprobante: string;
    numero: string;
    fecha: string | null;
    comprado: number;
    recibido: number;
    pendiente: number;
    /** La recepción de esa compra (id público); null = no tiene. */
    recepcionId: string | null;
    recepcionStatus: string | null;
  }>;
};

export async function listArticulosImportacion(id: number): Promise<ArticuloImportacion[]> {
  const r = await apiFetch<{ items: ArticuloImportacion[] }>(`/api/importaciones/${id}/articulos`);
  return r.items;
}

// ── Bitácora ─────────────────────────────────────────────────────────────

export type NotaImportacion = {
  id: number;
  usuario: string;
  texto: string;
  /** ISO con hora. */
  creadaEn: string;
};

/** `habilitado: false` = falta la migración: la pantalla oculta la bitácora. */
export type NotasImportacion = { habilitado: boolean; items: NotaImportacion[] };

const rutaNotas = (id: number) => `/api/importaciones/${id}/notas`;

export function listNotasImportacion(id: number): Promise<NotasImportacion> {
  return apiFetch<NotasImportacion>(rutaNotas(id));
}

export function crearNotaImportacion(id: number, texto: string): Promise<NotaImportacion> {
  return apiFetch<NotaImportacion>(rutaNotas(id), { method: 'POST', body: { texto } });
}

export async function eliminarNotaImportacion(id: number, notaId: number): Promise<void> {
  await apiFetch(`${rutaNotas(id)}/${notaId}`, { method: 'DELETE' });
}
